import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SHELF_CATEGORIES, kcalFromMacros, kcalLooksWrong } from '@kraftwerk/shared';
import type { ShelfCategory } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalFood } from '../../db/local.js';
import { createFood, updateFood } from '../../db/nutrition.js';
import { FoodLookup, suggestionSourceNote } from './FoodLookup.js';
import type { FoodSuggestion } from './FoodLookup.js';
import { NutrientFields, nutrientInput, nutrientsFromInput } from './NutrientFields.js';

/**
 * Ein Lebensmittel anlegen oder ändern.
 *
 * Neu (21.10.2026) in zwei Schritten: erst **finden** (Strichcode, Suche
 * oder „selbst eintragen“), dann das ausgefüllte Formular prüfen und
 * speichern. Vorher stand ein leeres Formular da und darüber „Nährwerte
 * nachschlagen“ — man suchte Nährwerte, bevor es ein Lebensmittel gab.
 */
export function FoodForm({
  food,
  onDone,
}: {
  food?: LocalFood;
  /** Mit der Kennung des gespeicherten Lebensmittels — ohne, wenn abgebrochen. */
  onDone: (foodId?: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [step, setStep] = useState<'find' | 'form'>(food ? 'form' : 'find');
  // Woher das Formular gefüllt wurde — steht über dem Formular, mit „anders suchen“.
  const [origin, setOrigin] = useState<
    { kind: 'picked'; source: 'off' | 'bls' } | { kind: 'unknownCode'; code: string } | null
  >(null);
  const nameId = useId();
  const brandId = useId();
  const sourceId = useId();
  const categoryId = useId();

  const [name, setName] = useState(food?.name ?? '');
  const [category, setCategory] = useState<ShelfCategory>(food?.category ?? 'other');
  const [brand, setBrand] = useState(food?.brand ?? '');
  const [kcal, setKcal] = useState(food?.kcalPer100g?.toString() ?? '');
  const [protein, setProtein] = useState(food?.proteinPer100g?.toString() ?? '');
  const [carbs, setCarbs] = useState(food?.carbsPer100g?.toString() ?? '');
  const [fat, setFat] = useState(food?.fatPer100g?.toString() ?? '');
  const [source, setSource] = useState(food?.sourceNote ?? t('nutrition.food.sourcePackage'));
  const [portionName, setPortionName] = useState(food?.portionName ?? '');
  const [portionGrams, setPortionGrams] = useState(food?.portionGrams?.toString() ?? '');
  const [barcode, setBarcode] = useState(food?.barcode ?? null);
  const [nutrients, setNutrients] = useState(nutrientInput(food?.nutrients));
  const portionNameId = useId();
  const portionGramsId = useId();

  /** Treffer aus Open Food Facts übernehmen — nur ins Formular, gespeichert wird von Hand. */
  const pick = (entry: FoodSuggestion) => {
    const field = (value: number | null) => (value === null ? '' : String(value));
    setName(entry.name);
    setBrand(entry.brand ?? '');
    setKcal(field(entry.kcalPer100g));
    setProtein(field(entry.proteinPer100g));
    setCarbs(field(entry.carbsPer100g));
    setFat(field(entry.fatPer100g));
    if (entry.portionGrams !== null) {
      setPortionName(entry.portionName ?? '');
      setPortionGrams(String(entry.portionGrams));
    }
    // Ein BLS-Code ist kein Strichcode — nur Packungen tragen einen.
    setBarcode(entry.source === 'bls' ? null : entry.code);
    setNutrients(nutrientInput(entry.nutrients));
    setSource(suggestionSourceNote(entry, new Date().toLocaleDateString('de-DE')));
    setOrigin({ kind: 'picked', source: entry.source ?? 'off' });
    setStep('form');
  };

  if (step === 'find') {
    return (
      <section
        aria-labelledby={`${nameId}-find`}
        className="flex flex-col gap-3 rounded-lg border border-accent bg-surface-raised p-3"
      >
        <h3 id={`${nameId}-find`} className="kw-kicker">
          {t('nutrition.food.findHeading')}
        </h3>
        <FoodLookup
          onPick={pick}
          onNotFound={(code) => {
            setBarcode(code);
            setOrigin({ kind: 'unknownCode', code });
            setStep('form');
          }}
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setOrigin(null);
              setStep('form');
            }}
            className="kw-btn kw-btn-quiet kw-btn-small flex-1"
          >
            {t('nutrition.food.manual')}
          </button>
          <button
            type="button"
            onClick={() => onDone()}
            className="kw-btn kw-btn-quiet kw-btn-small flex-1"
          >
            {t('nutrition.library.cancel')}
          </button>
        </div>
      </section>
    );
  }

  const entered = {
    kcal: toNumber(kcal) ?? 0,
    protein: toNumber(protein) ?? 0,
    carbs: toNumber(carbs) ?? 0,
    fat: toNumber(fat) ?? 0,
  };
  const warnKcal = kcalLooksWrong(entered);

  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-accent bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        const kcalValue = toNumber(kcal);
        if (name.trim() === '' || kcalValue === null) return;

        const draft = {
          name,
          brand: brand.trim() || null,
          kcalPer100g: kcalValue,
          proteinPer100g: toNumber(protein) ?? 0,
          carbsPer100g: toNumber(carbs) ?? 0,
          fatPer100g: toNumber(fat) ?? 0,
          sourceNote: source.trim() || null,
          category,
          // Portion nur mit beidem — „Scheibe" ohne Gramm wäre eine Einheit ohne Wert.
          portionName: portionName.trim() && toNumber(portionGrams) ? portionName.trim() : null,
          portionGrams:
            portionName.trim() && toNumber(portionGrams) ? toNumber(portionGrams) : null,
          barcode,
          nutrients: nutrientsFromInput(nutrients),
        };
        void (
          food
            ? updateFood(food.id, draft, db).then(() => food.id)
            : createFood(draft, db).then((row) => row.id)
        ).then((id) => onDone(id));
      }}
    >
      {food ? null : (
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs text-ink-muted">
            {origin?.kind === 'picked'
              ? t(`nutrition.food.fromSource.${origin.source}`)
              : origin?.kind === 'unknownCode'
                ? t('nutrition.food.withCode', { code: origin.code })
                : t('nutrition.food.byHand')}
          </p>
          <button
            type="button"
            onClick={() => setStep('find')}
            className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
          >
            {t('nutrition.food.findAgain')}
          </button>
        </div>
      )}

      <p className="text-xs text-ink-muted">{t('nutrition.food.per100gHint')}</p>

      <div className="flex flex-wrap gap-2">
        <label
          htmlFor={nameId}
          className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-ink-muted"
        >
          {t('nutrition.food.name')}
          <input
            id={nameId}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            maxLength={120}
            className="kw-field text-ink"
          />
        </label>
        <label htmlFor={brandId} className="flex w-32 flex-col gap-1 text-xs text-ink-muted">
          {t('nutrition.food.brand')}
          <input
            id={brandId}
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
            maxLength={80}
            className="kw-field text-ink"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <MacroField label={t('nutrition.macro.kcal')} value={kcal} onChange={setKcal} />
        <MacroField label={t('nutrition.macro.protein')} value={protein} onChange={setProtein} />
        <MacroField label={t('nutrition.macro.carbs')} value={carbs} onChange={setCarbs} />
        <MacroField label={t('nutrition.macro.fat')} value={fat} onChange={setFat} />
      </div>

      {warnKcal ? (
        <p role="alert" className="text-xs text-warn">
          {t('nutrition.recipe.kcalMismatch', { computed: Math.round(kcalFromMacros(entered)) })}
        </p>
      ) : null}

      <NutrientFields value={nutrients} onChange={setNutrients} basis={t('nutrients.per100g')} />

      <div className="flex flex-wrap gap-2">
        <label
          htmlFor={portionNameId}
          className="flex min-w-32 flex-1 flex-col gap-1 text-xs text-ink-muted"
        >
          {t('nutrition.food.portionName')}
          <input
            id={portionNameId}
            value={portionName}
            onChange={(event) => setPortionName(event.target.value)}
            placeholder={t('nutrition.food.portionNameHint')}
            maxLength={40}
            className="kw-field text-ink"
          />
        </label>
        <label htmlFor={portionGramsId} className="flex w-24 flex-col gap-1 text-xs text-ink-muted">
          {t('nutrition.food.portionGrams')}
          <input
            id={portionGramsId}
            inputMode="decimal"
            value={portionGrams}
            onChange={(event) => setPortionGrams(event.target.value)}
            className="kw-field text-ink"
          />
        </label>
      </div>

      <label htmlFor={categoryId} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('nutrition.food.category')}
        <select
          id={categoryId}
          value={category}
          onChange={(event) => setCategory(event.target.value as ShelfCategory)}
          className="kw-field text-ink"
        >
          {SHELF_CATEGORIES.map((option) => (
            <option key={option} value={option}>
              {t(`shopping.category.${option}`)}
            </option>
          ))}
        </select>
      </label>

      <label htmlFor={sourceId} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('nutrition.food.source')}
        <input
          id={sourceId}
          value={source}
          onChange={(event) => setSource(event.target.value)}
          maxLength={500}
          className="kw-field text-ink"
        />
      </label>

      <div className="flex gap-2">
        <button type="submit" className="kw-btn kw-btn-small">
          {t('nutrition.library.save')}
        </button>
        <button
          type="button"
          onClick={() => onDone()}
          className="rounded-lg border border-line px-3 py-2 text-sm text-ink-muted hover:border-accent"
        >
          {t('nutrition.library.cancel')}
        </button>
      </div>
    </form>
  );
}

export function MacroField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <label htmlFor={id} className="flex w-20 flex-col gap-1 text-xs text-ink-muted">
      {label}
      <input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="kw-field text-ink"
      />
    </label>
  );
}

export function toNumber(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
